import { MouseEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useRedux } from 'hooks';
import { activateWorkspaceTab, closeWorkspaceTab } from 'redux/workspaceTabs/actions';
import { WorkspaceTab } from 'redux/workspaceTabs/types';
import { getWorkspaceTabIcon } from './tabTitle';
import './workspaceTabs.css';

const WorkspaceTabBar = () => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState(false);
  const { dispatch, appSelector } = useRedux();
  const { tabs, activeKey } = appSelector((state) => ({
    tabs: state.WorkspaceTabs.tabs as WorkspaceTab[],
    activeKey: state.WorkspaceTabs.activeKey as string | null,
  }));

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const updateOverflow = () => {
      setCanScroll(el.scrollWidth > el.clientWidth + 1);
    };

    const onWheel = (event: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) {
        return;
      }

      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (!delta) {
        return;
      }

      event.preventDefault();
      el.scrollLeft += delta;
    };

    updateOverflow();
    el.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('resize', updateOverflow);
    return () => {
      el.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', updateOverflow);
    };
  }, [tabs.length]);

  if (!tabs.length) {
    return null;
  }

  const handleActivate = (tab: WorkspaceTab) => {
    if (tab.key === activeKey) {
      return;
    }
    dispatch(activateWorkspaceTab(tab.key));
    navigate(`${tab.pathname}${tab.search || ''}`);
  };

  const handleClose = (event: MouseEvent, tab: WorkspaceTab) => {
    event.preventDefault();
    event.stopPropagation();

    const index = tabs.findIndex((item) => item.key === tab.key);
    const wasActive = tab.key === activeKey;
    dispatch(closeWorkspaceTab(tab.key));

    if (!wasActive) {
      return;
    }

    const remaining = tabs.filter((item) => item.key !== tab.key);
    const next = remaining[index] || remaining[index - 1];
    if (next) {
      navigate(`${next.pathname}${next.search || ''}`);
    }
      // else {
      //   navigate('/dashboard/oprations');
      // }
  };

  const scrollBy = (distance: number) => {
    scrollRef.current?.scrollBy({ left: distance, behavior: 'smooth' });
  };

  return (
    <div className="workspace-tab-bar" role="tablist" aria-label="Open modules">
      <div className="workspace-tab-bar__scroll" ref={scrollRef}>
        {tabs.map((tab) => {
          const isActive = tab.key === activeKey;
          return (
            <div
              key={tab.key}
              role="tab"
              aria-selected={isActive}
              className={classNames('workspace-tab', { 'workspace-tab--active': isActive })}
              onClick={() => handleActivate(tab)}
              title={tab.title}
            >
              <span className="workspace-tab__icon" aria-hidden="true">
                <i className={getWorkspaceTabIcon(tab.pathname)} />
              </span>
              <span className="workspace-tab__title">{tab.title}</span>
              <button
                type="button"
                className="workspace-tab__close"
                aria-label={`Close ${tab.title}`}
                onClick={(event) => handleClose(event, tab)}
              >
                <i className="mdi mdi-close" />
              </button>
            </div>
          );
        })}
      </div>
      {canScroll && (
        <div className="workspace-tab-bar__nav">
          <button
            type="button"
            className="workspace-tab-bar__nav-btn"
            aria-label="Scroll tabs left"
            onClick={() => scrollBy(-160)}
          >
            <i className="mdi mdi-chevron-up" />
          </button>
          <button
            type="button"
            className="workspace-tab-bar__nav-btn"
            aria-label="Scroll tabs right"
            onClick={() => scrollBy(160)}
          >
            <i className="mdi mdi-chevron-down" />
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkspaceTabBar;
